import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Checkbox } from '@mui/material';
import { TextField } from 'new/elements/TextField';
import { SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { Search, Clear } from '@mui/icons-material';
import { InputAdornment, IconButton } from '@mui/material';

import { updateFormData } from 'js/actions';
import Typography from 'new/elements/Typography';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Virtuoso } from 'react-virtuoso';
import classes from './SourceSetupPage.module.scss';
import useLoadFeeds from './useLoadFeeds';
import { every, filter, forEach, isEmpty, pick } from 'lodash';
import { useGetPaging } from 'js/hooks/useGetPaging';
import CircularLoader from 'js/components/elements/progress/CircularLoader';
import { getDataFeedParams } from './getDataFeedParams';
import { getVendorFeedSpecs } from './getVendorFeedSpecs';
import { createVendorFeedSpecsWrapper } from './utils/vendorFeedSpecsUtils';
import {
  FeedField,
  FeedParamsType,
  SelectedFeedsType,
  VendorFeedSpecs,
} from 'new/components/flows/types/elt/CreateELTFlow/types';

import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import { useDistance } from 'new/hooks/useDistance';
import classNames from 'classnames';
import { Tooltip } from '@mui/material';
import InfoIcon from 'js/components/recipeBuilder/icons/InfoIcon';
import FeedItem from './FeedItem';
import usePaging from 'new/state/paging/repository/usePaging';
import useEntities from 'new/state/entities/repository/useEntities';
import { getFeedEndpoints } from 'new/utils/actionEndpoints';

const FeedsList = ({ formType, isEditMode, templateVersionInfo }) => {
  const { metaInfoById } = useGetPaging();
  const { data: vendorEndpoints } = useEntities('vendorEndpoints');
  const { data: vendors } = useEntities('vendors');
  const { data: resourceParameters } = useEntities('vendorEndpointParams');
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const refList = useRef();
  const height = useDistance(refList, 500, 'top');

  const formState = useSelector((state: any) => state.forms[formType]?.data || {});

  const { getSourceConfigFields } = usePaging();
  const parameters = getSourceConfigFields(formState.selectedConnection);
  const maxFeedsParameter: any = parameters.find(
    (param: any) => param.max_feeds === 0 || !!param.max_feeds,
  );
  const maxFeeds = !formState.sourceConfig?.[maxFeedsParameter?.name]
    ? maxFeedsParameter?.max_feeds
    : undefined;

  const { availableFeeds, allFeedsFetched, discoveryErrors, userMessages, validationResult } =
    useMemo(() => {
      const result = {
        availableFeeds: formState.discoveryResults?.availableFeeds || [],
        discoveryErrors: formState.discoveryResults?.discoveryErrors || [],
        allFeedsFetched: formState.discoveryResults?.allFeedsFetched || false,
        userMessages: formState.discoveryResults?.userMessages,
        validationResult: formState.discoveryResults?.validationResult,
      };

      return result;
    }, [formState.discoveryResults]);

  const selectedFeeds = useSelector((state: any) => {
    return state.forms[formType]?.data?.selectedFeeds;
  });

  //TODO availableFeedsByValue might not be necessary
  // const availableFeedsByValue = useMemo(
  //   () => mapKeys(availableFeeds, ({ value }) => value),
  //   [availableFeeds]
  // );

  // Get the load feeds function from useLoadFeeds
  const loadFeeds = useLoadFeeds(formType, isEditMode, templateVersionInfo);
  const selectedConnection = useSelector(
    (state: any) => state.forms[formType]?.data?.selectedConnection,
  );
  const currentBulkUpdateSpecValue = useSelector(
    (state: any) => state.forms[formType]?.data?.bulkUpdateSpecValue,
  );
  const { sourceConfigById } = useGetPaging();
  const [search, setSearch] = useState('');
  // Default to "All Feeds" when creating new source, "Selected" when editing
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [sortColumn, setSortColumn] = useState('value');
  const [sortDirection, setSortDirection] = useState('asc');

  // Saved (edit) or defaulted (create) endpoint; kept even if it was later marked as an action
  const selectedEndpointId = formState.sourceConfig?.vendor_endpoint_id;

  // Create a memoized vendor info hook that processes vendor data and endpoints once
  const vendorInfo = useMemo(() => {
    const { vendorId } = metaInfoById[selectedConnection] || {};
    if (!vendorId || !vendors || !vendorEndpoints) {
      return null;
    }

    const vendor = vendors[vendorId];
    if (!vendor) {
      return null;
    }

    // Process endpoints once
    const endpointIds = vendor.vendor_endpoints || [];
    const endpoints = pick(vendorEndpoints, endpointIds);

    //we should enrich resource_parameters for each endpoint with the resource_parameter object. right  now it is just the id.
    const enrichedEndpoints = Object.values(endpoints).map((endpoint) => {
      const resource_parameters = endpoint.resource_parameters;
      const enrichedResourceParameters = resource_parameters
        .map((resource_parameter) => resourceParameters[resource_parameter])
        .filter((resource_parameter) => resource_parameter !== undefined);
      return { ...endpoint, resource_parameters: enrichedResourceParameters };
    });

    return {
      vendor, // The full vendor object
      endpoints: {
        all: enrichedEndpoints,
        discovery: filter(
          enrichedEndpoints,
          ({ source_template }) => source_template.endpoint_type === 'discovery',
        ),
        // ELT is an ETL source: action endpoints don't act as feeds, except one the source uses
        feeds: getFeedEndpoints(enrichedEndpoints, { keepIds: [selectedEndpointId] }),
        discoveryFields: filter(
          enrichedEndpoints,
          ({ source_template }) => source_template.endpoint_type === 'discovery_fields',
        ),
      },
    };
  }, [
    selectedConnection,
    metaInfoById,
    vendors,
    vendorEndpoints,
    resourceParameters,
    selectedEndpointId,
  ]);

  // Compute vendorFeedSpecs and save to Redux for other components to use
  const vendorFeedSpecs: VendorFeedSpecs = useMemo(() => {
    if (!vendorInfo) {
      // Return empty specs if vendor info not available yet
      return {
        ref_feed_options: {
          display: {},
          feed_names: {},
          ref_field_list_selector: '',
          ref_fields_names: '',
          default_ref_feed: null,
          default_include_feeds: [],
          supports_force_resync: {},
          // TODO: These might be V1-specific hardcoded values - consider removing for V2
          // object_detection_mode_selector_param: undefined,
          // single_schema_param: undefined
        },
        data_feed_options: {},
        capabilities: {},
        additional_params: [],
      };
    }

    return getVendorFeedSpecs(
      sourceConfigById,
      selectedConnection,
      availableFeeds,
      templateVersionInfo, // Pass template version info directly
      vendorInfo.vendor, // The vendor object
      vendorInfo.endpoints, // The processed endpoints
    );
  }, [sourceConfigById, selectedConnection, availableFeeds, templateVersionInfo, vendorInfo]);

  // Create wrapper for type-safe access to vendor feed specs
  const vendorFeedSpecsWrapper = useMemo(() => {
    return createVendorFeedSpecsWrapper(
      vendorFeedSpecs,
      templateVersionInfo?.templateVersion || 'v1',
    );
  }, [vendorFeedSpecs, templateVersionInfo?.templateVersion]);

  // Check if any feeds support force_resync using the wrapper
  const hasForceResyncSupport = useMemo(() => {
    const support = Object.values(vendorFeedSpecsWrapper.getForceResyncSupport()).some(
      (supports) => supports === true,
    );

    return support;
  }, [vendorFeedSpecsWrapper]);

  // TODO: This might be V1-specific hardcoded logic - consider using V2 capabilities instead
  const isFieldsEndpointAvailable = useMemo(() => {
    // For V2 templates, field selection is controlled by FIELD_SELECTION capability
    if (vendorFeedSpecsWrapper?.isV2Template()) {
      const v2Specs = vendorFeedSpecs as any;
      const fieldSelectionCapability = v2Specs.capabilities?.FIELD_SELECTION;
      return fieldSelectionCapability?.enabled || false;
    }
    // For V1 templates, use the old logic
    return !isEmpty(vendorInfo?.endpoints.discoveryFields);
  }, [vendorInfo?.endpoints.discoveryFields, vendorFeedSpecsWrapper, vendorFeedSpecs]);

  //get all possible base specs using the wrapper for type-safe access
  const allBaseSpecOptions = useMemo(() => {
    if (vendorFeedSpecsWrapper.isV2Template()) {
      // For V2: Show strategy names but use ref_feed values for bulk updates
      const v2Specs = vendorFeedSpecs as any;
      const strategies = v2Specs.ref_feed_options?.strategies || {};
      const strategyMappings = v2Specs.strategyMappings || {};

      // Convert strategies to options, using strategy mappings to get ref_feed values
      return Object.entries(strategies).map(([strategyKey, strategyInfo]: [string, any]) => {
        const strategyName = strategyInfo?.label || strategyKey;
        const strategyRefFeeds = strategyMappings[strategyKey]?.ref_feeds || [];

        // For bulk updates, use the first ref_feed from the strategy
        // This will be applied to all feeds that support this strategy
        const refFeed = strategyRefFeeds[0] || strategyKey;

        return {
          label: strategyName,
          value: refFeed, // Save the ref_feed value for bulk updates
        };
      });
    } else {
      // For V1: Use display options as before
      return Object.values(vendorFeedSpecsWrapper.getDisplayOptions()) || [];
    }
  }, [vendorFeedSpecsWrapper, vendorFeedSpecs]);

  // Save vendorFeedSpecs to Redux whenever it changes
  useEffect(() => {
    if (
      vendorFeedSpecs &&
      selectedConnection &&
      Object.keys(vendorFeedSpecs.ref_feed_options).length > 0
    ) {
      // Check if we have a valid default value (either V1 or V2)
      // TODO: This might be V1-specific hardcoded logic - consider using wrapper methods
      const hasValidDefault = vendorFeedSpecsWrapper?.getDefaultRef();

      if (hasValidDefault) {
        dispatch(updateFormData(formType, { vendorFeedSpecs }));
      }
    } else if (!selectedConnection) {
      // Clear vendorFeedSpecs when connection changes
      dispatch(updateFormData(formType, { vendorFeedSpecs: null }));
    }
  }, [vendorFeedSpecs, selectedConnection, formType]);

  const onFeedFieldSelectorChanged = ({
    feed,
    field,
    isChecked,
    feedParams,
    allFields,
  }: {
    feed: string;
    field: string;
    isChecked: boolean;
    feedParams: FeedParamsType;
    allFields: FeedField[];
  }) => {
    const newSelectedFeeds: SelectedFeedsType = { ...selectedFeeds };
    if (isChecked && (!maxFeeds || (maxFeeds && Object.keys(newSelectedFeeds).length < maxFeeds))) {
      newSelectedFeeds[feed] = {
        feed_selected: true,
        allFields,
        feed_display_name: availableFeeds.find(({ value }) => value === feed)?.label,
        feed_type: 'ref_feed',
        ...feedParams,
        ...newSelectedFeeds?.[feed],
        selectedFields: {
          ...newSelectedFeeds?.[feed]?.selectedFields,
          [field]: isChecked,
        },
      };
    } else {
      delete newSelectedFeeds?.[feed]?.selectedFields?.[field];
      if (Object.keys(newSelectedFeeds?.[feed]?.selectedFields || {}).length === 0) {
        delete newSelectedFeeds?.[feed];
      }
    }
    dispatch(updateFormData(formType, { selectedFeeds: newSelectedFeeds }));
  };

  const onSelectAllFields = ({
    feed,
    fieldValues,
    isChecked,
    allFields,
  }: {
    feed: string;
    fieldValues: string[];
    isChecked: boolean;
    allFields: FeedField[];
  }) => {
    const newSelectedFeeds: SelectedFeedsType = { ...selectedFeeds };
    newSelectedFeeds[feed] = {
      ...newSelectedFeeds?.[feed],
      allFields,
      selectedFields: isChecked
        ? fieldValues.reduce((acc, field) => ({ ...acc, [field]: true }), {})
        : {},
    };
    dispatch(updateFormData(formType, { selectedFeeds: newSelectedFeeds }));
  };

  const onExpandFeed = (feed: string, isExpanded: boolean) => {
    dispatch(
      updateFormData(formType, {
        expandedFeeds: {
          ...formState.expandedFeeds,
          [feed]: isExpanded,
        },
      }),
    );
  };

  const onFeedSelectorChanged = (
    feed: string,
    {
      isChecked = false,
      feedLabel = feed,
      selectedFields = {},
      allFields = [],
      feedParams = {
        ref_spec_name: '',
        primary_keys: '',
        ref_spec_resource_name: '',
      },
    }: {
      isChecked?: boolean;
      feedLabel?: string;
      allFields?: FeedField[];
      selectedFields?: Record<string, boolean>;
      feedParams?: FeedParamsType;
    },
  ) => {
    const newSelectedFeeds: SelectedFeedsType = { ...selectedFeeds };

    if (isChecked && ((maxFeeds && Object.keys(newSelectedFeeds).length < maxFeeds) || !maxFeeds)) {
      newSelectedFeeds[feed] = {
        feed_selected: true,
        allFields,
        feed_display_name: feedLabel,
        feed_type: 'ref_feed',
        selectedFields,
        ...feedParams,
      };
    } else {
      // Instead of deleting the feed, mark it as not selected
      // The service will handle setting skip_feed: true
      if (newSelectedFeeds[feed]) {
        newSelectedFeeds[feed] = {
          ...newSelectedFeeds[feed],
          feed_selected: false,
        };
      }
    }

    dispatch(updateFormData(formType, { selectedFeeds: newSelectedFeeds }));
  };

  const onFeedPropsChanged = (feed: string, newProps) => {
    const newSelectedFeeds: SelectedFeedsType = { ...selectedFeeds };
    let currentFeedProps = newSelectedFeeds[feed];
    if (!currentFeedProps) {
      return;
    }

    newSelectedFeeds[feed] = { ...currentFeedProps, ...newProps };
    dispatch(updateFormData(formType, { selectedFeeds: newSelectedFeeds }));
  };

  const onSelectAllChanged = (event, isChecked) => {
    let newSelectedFeeds: SelectedFeedsType = { ...selectedFeeds };

    if (isChecked) {
      // Only select/deselect the currently filtered feeds
      forEach(filteredAvailableFeeds, ({ value, label }) => {
        const feedParams = getDataFeedParams(selectedFeeds, value, vendorFeedSpecs);

        if (feedParams.ref_spec_name !== '' || feedParams.ref_spec_resource_name !== '') {
          newSelectedFeeds[value] = {
            feed_selected: isChecked,
            feed_display_name: label,
            feed_type: 'ref_feed',
            selectedFields: {},
            ...feedParams,
          };
        }
      });
    } else {
      // Only deselect the currently filtered feeds, preserve others
      forEach(filteredAvailableFeeds, ({ value }) => {
        if (newSelectedFeeds[value]) {
          newSelectedFeeds[value] = {
            ...newSelectedFeeds[value],
            feed_selected: false,
          };
        }
      });
    }

    dispatch(updateFormData(formType, { selectedFeeds: newSelectedFeeds }));
  };

  const onBaseSpecBulkUpdate = (newValue) => {
    const newSelectedFeeds: SelectedFeedsType = { ...selectedFeeds };

    forEach(newSelectedFeeds, (feedProps, feed) => {
      //check if the newValue is allowed for this feed
      const thisFeedOptions = vendorFeedSpecs?.data_feed_options[feed];

      if (vendorFeedSpecsWrapper.isV2Template()) {
        // For V2: Check if the ref_feed is in allowed_reference_feeds
        const v2FeedOptions = thisFeedOptions as any;
        if (!v2FeedOptions?.allowed_reference_feeds?.includes(newValue) && newValue !== '') {
          return;
        }
      } else {
        // For V1: Check ref_feed_selectors as before
        // TODO: This might be V1-specific hardcoded logic - consider using wrapper methods
        if (!thisFeedOptions?.ref_feed_selectors?.includes(newValue) && newValue !== '') {
          return;
        }
      }

      newSelectedFeeds[feed] = {
        ...feedProps,
        ref_spec_name: newValue,
      };
    });

    dispatch(
      updateFormData(formType, {
        selectedFeeds: newSelectedFeeds,
        bulkUpdateSpecValue: newValue,
      }),
    );
  };
  const onSearchInputChanged = (search) => {
    setSearch(search);
  };

  // Handle column sorting
  const handleColumnSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const itemRenderer = useCallback(
    (index, { label, value }) => {
      return (
        <FeedItem
          key={value}
          label={label}
          value={value}
          formType={formType}
          vendorFeedSpecs={vendorFeedSpecs}
          selectedFeeds={selectedFeeds}
          enableFieldSelection={isFieldsEndpointAvailable}
          onFeedSelectorChanged={onFeedSelectorChanged}
          onFeedPropsChanged={onFeedPropsChanged}
          onFeedFieldSelectorChanged={onFeedFieldSelectorChanged}
          onSelectAllFields={onSelectAllFields}
          onExpandFeed={onExpandFeed}
          templateVersionInfo={templateVersionInfo}
        />
      );
    },
    [
      formType,
      vendorFeedSpecs,
      selectedFeeds,
      isFieldsEndpointAvailable,
      onFeedSelectorChanged,
      onFeedPropsChanged,
      onFeedFieldSelectorChanged,
      onSelectAllFields,
      onExpandFeed,
      templateVersionInfo,
    ],
  );

  // Sorting function for feeds
  const sortFeeds = useCallback(
    (feeds, column, direction) => {
      const sortedFeeds = [...feeds];

      return sortedFeeds.sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (column) {
          case 'label':
            aValue = (a.label || '').replace(/^[_\-\s]+/, '').toLowerCase();
            bValue = (b.label || '').replace(/^[_\-\s]+/, '').toLowerCase();
            break;
          case 'value':
            aValue = (a.value || '').toLowerCase();
            bValue = (b.value || '').toLowerCase();
            break;
          case 'feedIngestionMode':
            // For feed ingestion mode, we'll sort by the ref_spec if available
            aValue = selectedFeeds?.[a.value]?.ref_spec_name || '';
            bValue = selectedFeeds?.[b.value]?.ref_spec_name || '';
            break;
          case 'uniqueIdentifier':
            // For unique identifier, sort by primary keys
            aValue = selectedFeeds?.[a.value]?.primary_keys || '';
            bValue = selectedFeeds?.[b.value]?.primary_keys || '';
            break;
          default:
            aValue = (a.value || '').toLowerCase();
            bValue = (b.value || '').toLowerCase();
        }

        if (direction === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
        }
      });
    },
    [selectedFeeds],
  );

  const filteredAvailableFeeds = useMemo(() => {
    let filtered = availableFeeds;

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered?.filter(({ label, value }) => {
        return label?.toLowerCase().includes(searchLower) || value.includes(search);
      });
    }

    // Apply selection filter
    if (showOnlySelected) {
      filtered = filtered?.filter(({ value }) => selectedFeeds?.[value]?.feed_selected === true);
    }
    // 'all' filter shows everything (no additional filtering)

    // Apply sorting
    return sortFeeds(filtered, sortColumn, sortDirection);
  }, [
    availableFeeds,
    search,
    showOnlySelected,
    selectedFeeds,
    sortColumn,
    sortDirection,
    sortFeeds,
  ]);

  // Calculate areAllFeedsSelected based on filtered feeds
  const areAllFeedsSelected = useMemo(
    () =>
      every(
        filteredAvailableFeeds,
        ({ value }) => selectedFeeds?.[value]?.feed_selected === true,
      ) && filteredAvailableFeeds.length > 0,
    [filteredAvailableFeeds, selectedFeeds],
  );

  const feedCount = availableFeeds?.length || 0;
  const selectedFeedCount = Object.values(selectedFeeds || {}).filter(
    (feed: any) => feed.feed_selected,
  ).length;

  // Helper function to get search results info text
  const getSearchResultsInfo = useCallback(() => {
    if (!search || !availableFeeds?.length) return null;

    const totalCount = showOnlySelected ? selectedFeedCount : feedCount;

    const feedType = showOnlySelected ? 'selected feeds' : 'available feeds';

    return `Showing ${filteredAvailableFeeds?.length || 0} of ${totalCount} ${feedType}`;
  }, [
    search,
    availableFeeds?.length,
    showOnlySelected,
    selectedFeedCount,
    feedCount,
    filteredAvailableFeeds?.length,
  ]);

  // Helper function to get search placeholder text
  const getSearchPlaceholder = useCallback(() => {
    if (showOnlySelected) return 'Search selected feeds...';
    return 'Search all feeds...';
  }, [showOnlySelected]);

  if (!allFeedsFetched) {
    return (
      <div className={classes.feedsList}>
        <div className="connecting-mode">
          <div className="connecting-indicator">
            <CircularLoader size={308} thickness={20} style={{ marginTop: -3 }} />
          </div>
          <p className="connecting-message">Loading Feeds ...</p>
        </div>
      </div>
    );
  }

  let currentBulkIngestionValue =
    currentBulkUpdateSpecValue || vendorFeedSpecsWrapper.getDefaultRef() || '';

  let currentBulkIngestionOption = getCurrentSelectedOption(
    allBaseSpecOptions,
    currentBulkIngestionValue,
  );
  //show bulk select if maxFeeds is not set or if it is set and maxFeeds count is greater  than available feeds count.
  const shouldShowBulkSelect = !maxFeeds || maxFeeds >= availableFeeds.length;
  return (
    <div className={classes.feedsList}>
      <div
        className={classes.feedsListTitle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="h6" className={classes.feedsTitleText}>
          Feeds{' '}
        </Typography>
        {maxFeeds && (
          <Typography variant="body2" style={{ fontWeight: 'var(--font-weight-medium)' }}>
            Max Feeds Allowed: {maxFeeds}
          </Typography>
        )}
      </div>
      {/* Controls Row - Search and filters on same horizontal line */}
      <div className={classes.feedsControlsRow}>
        {/* Left side: Selection controls */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-md)',
          }}
        ></div>

        {/* Search field */}
        <TextField
          value={search}
          onChange={(e) => onSearchInputChanged(e.target.value)}
          placeholder={getSearchPlaceholder()}
          size="small"
          style={{ width: '280px', maxWidth: '280px', marginRight: 'var(--spacing-lg)' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),

            endAdornment: (
              <InputAdornment position="end">
                {search && (
                  <IconButton size="small" onClick={() => onSearchInputChanged('')} edge="end">
                    <Clear fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        <SegmentedControl
          controls={[
            {
              label: `All Feeds (${feedCount})`,
              value: 'all',
            },
            {
              label: `Selected (${selectedFeedCount})`,
              value: 'selected',
            },
          ]}
          defaultValue={isEditMode ? 'selected' : 'all'}
          value={showOnlySelected ? 'selected' : 'all'}
          onControlChange={(_, value) => {
            setShowOnlySelected(value === 'selected');
          }}
        />

        {/* Right side: Bulk update controls */}
        {allBaseSpecOptions.length > 1 &&
          Object.values(selectedFeeds || {}).filter((feed: any) => feed.feed_selected).length >
            1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-md)',
                marginLeft: 'auto',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-base-sm)',
                  color: 'var(--color-greyscale-600)',
                }}
              >
                Bulk Update Ingestion Mode
              </span>
              <Tooltip
                title={
                  'Use this dropdown to bulk update the Feed Ingestion Mode for all selected feeds at once. You can still customize individual feeds after bulk update.'
                }
              >
                <div className={classes.infoIconContainer}>
                  <InfoIcon />
                </div>
              </Tooltip>
              <SortedSelect
                placeholder="Choose Ingestion Mode"
                value={currentBulkIngestionOption}
                options={allBaseSpecOptions}
                components={{ Input: SelectInput, Option: SelectOption }}
                styles={{
                  ...redesignStyles,
                  container: (styles) => ({
                    ...styles,
                    minHeight: 24,
                    minWidth: 160,
                  }),
                  dropdownIndicator: (styles, state) => ({
                    ...styles,
                    color: 'var(--color-greyscale-600)',
                    '&:hover': {
                      color: 'var(--color-greyscale-600)',
                    },
                  }),
                }}
                isClearable={false}
                backspaceRemovesValue={false}
                isSearchable
                onChange={(option) => {
                  let newValue = getNewSelectionValue(option, true);
                  onBaseSpecBulkUpdate(newValue);
                }}
              />
            </div>
          )}
      </div>

      {/* Search Results Info - positioned below the entire controls row to prevent jumping */}
      {getSearchResultsInfo() && (
        <div
          style={{
            fontSize: 'var(--font-size-base-sm)',
            color: 'var(--color-greyscale-500)',
            padding: 'var(--spacing-sm) var(--spacing-xl)',
            backgroundColor: 'var(--color-greyscale-25)',
            borderTop: '1px solid var(--color-greyscale-100)',
            lineHeight: 'var(--line-height-sm)',
          }}
        >
          {getSearchResultsInfo()}
        </div>
      )}

      {/* Feed Discovery Warning Banner */}
      {!isEmpty(discoveryErrors) && !isEmpty(availableFeeds) && (
        <div className={classes.feedsWarningContainer}>
          <div className={classes.feedsWarningTitleIcon}></div>
          <div className={classes.feedsWarningContent}>
            <div className={classes.feedsWarningTitle}></div>
            <div className={classes.feedsWarningSubtitle}>
              Unable to fetch latest feeds, but showing your existing feed configuration. You can
              still edit and manage your configured feeds.
            </div>
            <button className={classes.feedsWarningActionButton} onClick={loadFeeds}>
              Retry Fetching Feeds
            </button>
          </div>
        </div>
      )}

      <div className={classes.feedsListHeaderRow}>
        <div className={classes.headerSpacer}>
          {shouldShowBulkSelect && (
            <Checkbox
              id="select-all-feeds-checkbox"
              checked={areAllFeedsSelected}
              onChange={onSelectAllChanged}
            />
          )}
        </div>
        <div
          className={classNames(classes.columnHeader, 'sortable-header')}
          onClick={() => handleColumnSort('label')}
          style={{ cursor: 'pointer' }}
        >
          Display Name
          {sortColumn === 'label' && (
            <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </div>
        <div
          className={classNames(classes.columnHeader, 'sortable-header')}
          onClick={() => handleColumnSort('value')}
          style={{ cursor: 'pointer' }}
        >
          Code Name
          {sortColumn === 'value' && (
            <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </div>
        <div
          className={classNames(classes.columnHeader, 'sortable-header')}
          onClick={() => handleColumnSort('feedIngestionMode')}
          style={{ cursor: 'pointer' }}
        >
          Feed Ingestion Mode
          {sortColumn === 'feedIngestionMode' && (
            <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </div>
        <div
          className={classNames(classes.columnHeader, 'sortable-header')}
          onClick={() => handleColumnSort('uniqueIdentifier')}
          style={{ cursor: 'pointer' }}
        >
          Unique Identifier
          {sortColumn === 'uniqueIdentifier' && (
            <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
        </div>
        <div
          className={classNames(classes.columnHeader, {
            [classes.hidden]: !hasForceResyncSupport,
          })}
        >
          Resync?
        </div>
      </div>
      <div ref={refList} style={{ height: height - 16 }} className={classes.feedsListBody}>
        {/* Show error message inside the feeds list body when discovery fails */}
        {isEmpty(availableFeeds) && !isEmpty(discoveryErrors) ? (
          <div className={classes.feedsErrorContainer}>
            <div className={classes.feedsErrorTitleIcon}>⚠️</div>
            <Typography variant="h5" className={classes.feedsErrorTitle}>
              {userMessages?.title || 'Unable to Load Feeds'}
            </Typography>
            <Typography variant="body1" className={classes.feedsErrorSubtitle}>
              {userMessages?.message ||
                'We encountered an issue while connecting to your data source. This could be due to connection settings, network issues, or temporary service unavailability.'}
            </Typography>

            {/* Error Details Section - Only show if there are meaningful errors */}
            {userMessages?.suggestions && userMessages.suggestions.length > 0 && (
              <div className={classes.feedsErrorDetailsContainer}>
                <Typography variant="body2" className={classes.feedsErrorDetailsTitle}>
                  Suggestions:
                </Typography>
                {userMessages.suggestions.map((suggestion, index) => (
                  <div key={index} className={classes.feedsErrorDetailsItem}>
                    • {suggestion}
                  </div>
                ))}
              </div>
            )}

            {/* Show validation issues if available */}
            {validationResult?.issues && validationResult.issues.length > 0 && (
              <div className={classes.feedsErrorDetailsContainer}>
                <Typography variant="body2" className={classes.feedsErrorDetailsTitle}>
                  Technical Details:
                </Typography>
                {validationResult.issues.map((issue, index) => (
                  <div key={index} className={classes.feedsErrorDetailsItem}>
                    <strong>{issue.category}:</strong> {issue.message}
                    {issue.suggestion && (
                      <div
                        style={{
                          marginLeft: 'var(--spacing-lg)',
                          fontSize: '0.9em',
                          color: '#666',
                        }}
                      >
                        💡 {issue.suggestion}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Action Button */}
            <div className={classes.feedsErrorActionButtonContainer}>
              <button onClick={loadFeeds} className={classes.feedsErrorActionButton}>
                {userMessages?.canRetry ? 'Try Again' : 'Refresh'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Show warning banner if there are validation issues but connector can still be used */}
            {(() => {
              const shouldShowWarning =
                (validationResult && !validationResult.isValid && validationResult.canProceed) ||
                (discoveryErrors.length > 0 && !isEmpty(availableFeeds));

              return shouldShowWarning;
            })() && (
              <div className={classes.feedsWarningContainer}>
                <div className={classes.feedsWarningIcon}>⚠️</div>
                <div className={classes.feedsWarningContent}>
                  <Typography variant="body2" className={classes.feedsWarningTitle}>
                    {userMessages?.title ||
                      (discoveryErrors.length > 0
                        ? 'Connection Issues Detected'
                        : 'Data Processing Issues Detected')}
                  </Typography>
                  <Typography variant="body2" className={classes.feedsWarningSuggestions}>
                    {userMessages?.message ||
                      (discoveryErrors.length > 0
                        ? 'Some data feeds may have incomplete information due to connection issues. We recommend refreshing to ensure you get the most accurate data.'
                        : 'Some feeds may have incomplete or incorrect information due to data format changes.')}
                  </Typography>
                  {userMessages?.suggestions && userMessages.suggestions.length > 0 && (
                    <div className={classes.feedsWarningSuggestions}>
                      {userMessages.suggestions.slice(0, 2).map((suggestion, index) => (
                        <div key={index} className={classes.feedsWarningSuggestion}>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Add a refresh button for data processing issues */}
                {(validationResult?.issues.some((issue) => issue.category === 'jsonpath') ||
                  discoveryErrors.length > 0) && (
                  <div className={classes.feedsWarningActionContainer}>
                    <button onClick={loadFeeds} className={classes.feedsWarningRefreshButton}>
                      Refresh Connection
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Show feeds when available */}
            {!isEmpty(filteredAvailableFeeds) && (
              <Virtuoso
                customScrollParent={refList.current}
                data={filteredAvailableFeeds}
                itemContent={itemRenderer}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FeedsList;
